import './index.css';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { BottomSheet } from 'react-spring-bottom-sheet';
import { withdrawMoney } from '../../actions/account-actions-types';
import { showToast } from '../../actions/app-actions-types';
import EventEmitter from '../../utilities/EventEmitter';
import { isMobileBrowser } from '../../utilities/helper';
import useTheme from '../../utilities/hooks/theme';
import I18n from '../../utilities/locale';
import View from '../view';
import Constants from '../../constants';
import Text from '../text';
import Button from '@mui/material/Button/Button';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField/TextField';
import React from 'react';
import Regex from '../../utilities/Regex';

interface Props {
  bankAccountId: any;
  visible: any;
  onPressDismiss: any;
  playWallet: any;
}

const WithdrawModal = ({ bankAccountId = '', visible, onPressDismiss, playWallet }: Props) => {
  const bottomSheetWidth = isMobileBrowser ? 'root_mobile' : 'root';
  const className = `${bottomSheetWidth}`;
  const { AccountTheme } = useTheme();
  const [amountError, setAmountError] = useState(null as any);
  const [amount, setAmount] = useState('' as any);
  const [withdrawError, setWithdrawError] = useState('');
  const dispatch = useDispatch();

  const onPressWithdrawMoney = () => {
    if (amount === '') {
      setAmountError('Please enter amount');
    } else if (parseInt(amount) < 200) {
      setAmountError(I18n.t('WithdrawMoneyValidation'));
    } else if (parseInt(amount) > playWallet) {
      setAmountError('Please enter amount less than balance');
    } else if (bankAccountId.length === 0) {
      setWithdrawError('Please verify your bank account');
    } else {
      withdrawProceed();
    }
  };

  const withdrawProceed = () => {
    const purpose = 'IGamio Bank withdrawal';
    const destination = 'bank';
    const beneficiaryId = bankAccountId;

    const data = {
      amount: amount * 100,
      destination,
      beneficiaryId,
      comment: purpose
    };
    dispatch(
      withdrawMoney({
        data,
        callback: ({ error }: { error: any }) => {
          if (!error) {
            onPressDismiss();
            EventEmitter.emit('BalanceUpdate');
            dispatch(showToast(I18n.t('withdrawRequestSentSuccessfully')));
          } else if (error.message) {
            setWithdrawError(error.message);
          } else {
            setWithdrawError(I18n.t('SomethingWentWrong'));
          }
        }
      })
    );
  };

  const onChangeAmount = (event: any) => {
    if (Regex.validateNumber(event.target.value) || event.target.value === '') {
      setAmount(event.target.value);
      if (amountError !== '') setAmountError(null);
    }
  };

  const containerColor = {
    backgroundColor: AccountTheme.withdrawContainer
  };

  const withdrawMoneyTitleColor = {
    color: AccountTheme.withdrawTitle
  };

  const minimumWithdrawLabelColor = {
    color: AccountTheme.withdrawMinAmountLabel
  };

  const verticalDivider = {
    backgroundColor: AccountTheme.withdrawVerticalDivider
  };

  const withdrawCurrentBalance = {
    color: AccountTheme.withdrawCurrentBalance
  };

  const withdrawCurrentBalanceValue = {
    color: AccountTheme.withdrawCurrentBalanceValue
  };

  const textInputColor = {
    backgroundColor: AccountTheme.simpleTextInputBG,
    color: AccountTheme.simpleTextInput
  };

  const inputSx: SxProps<Theme> = {
    '& .MuiInputBase-input': {
      color: AccountTheme.simpleTextInput,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'uppercase'
    },
    '& .MuiFormHelperText-root.Mui-error': {
      backgroundColor: AccountTheme.addCashContainer,
      margin: 0,
      color: AccountTheme.simpleTextInputError,
      paddingLeft: '5px',
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiInputLabel-root': {
      color: AccountTheme.simpleTextInput,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: AccountTheme.simpleTextInput,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& input::placeholder': {
      color: AccountTheme.simpleTextInputPlaceholder,
      fontSize: '1.1rem',
      textTransform: 'none'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: AccountTheme.simpleTextInput
      },
      '&:hover fieldset': {
        borderColor: AccountTheme.simpleTextInput
      },
      '&.Mui-focused fieldset': {
        borderColor: AccountTheme.simpleTextInput,
        borderWidth: '1px'
      }
    }
  };

  return (
    <BottomSheet className={`${className}`} onDismiss={onPressDismiss} open={visible}>
      <View className={'withdraw-modal-container'} style={containerColor}>
        <View className={'withdraw-modal-header-wrapper'}>
          <View style={{ position: 'absolute', left: 15 }} onPress={onPressDismiss}>
            <Constants.Svgs.ModalBack width={20} height={20} fill={AccountTheme.withdrawBackBtn} />
          </View>
          <Text className={'withdraw-modal-header-title'} style={withdrawMoneyTitleColor}>
            {I18n.t('WithDrawMoney')}
          </Text>
        </View>
        <View className={'withdraw-modal-divider'} style={verticalDivider} />
        <View className={'withdraw-modal-balance-wrapper'}>
          <Text className={'withdraw-modal-balance-title'} style={withdrawCurrentBalance}>
            {'Current Balance'}
          </Text>
          <Text
            className={'withdraw-modal-balance-value'}
            style={withdrawCurrentBalanceValue}>{` : ₹${playWallet}`}</Text>
        </View>
        <View className={'withdraw-modal-content-wrapper'}>
          <TextField
            style={textInputColor}
            placeholder={I18n.t('EnterWithdrawAmount')}
            value={amount}
            className={'withdraw-modal-text-input'}
            label={I18n.t('EnterWithdrawAmount')}
            variant="outlined"
            size={'small'}
            onChange={onChangeAmount}
            sx={inputSx}
            error={amountError ? true : false}
            helperText={amountError}
            autoCapitalize={'none'}
            type="tel"
            // type={'number'}
          />
          <Text className={'withdraw-modal-min-withdraw-label'} style={minimumWithdrawLabelColor}>
            {I18n.t('MinimumWithdrawalAmount')}
          </Text>
          <View className={'withdraw-modal-sub-wrapper'}>
            <View
              className={'withdraw-modal-bank-wrapper'}
              style={{
                backgroundColor: AccountTheme.withdrawInputBG,
                borderColor: AccountTheme.withdrawOptionBorder
              }}
              onPress={() => {
                if (bankAccountId) {
                  setWithdrawError('');
                } else setWithdrawError('Please verify your bank account to withdraw through bank');
              }}>
              <Constants.Svgs.Bank
                width={40}
                height={30}
                fillPrimary={AccountTheme.svgPrimary}
                fillSecondary={AccountTheme.svgSecondary}
              />
              <Text
                className={'withdraw-modal-pay-transfer-title'}
                style={{ color: AccountTheme.withdrawOptionTitle }}>
                {I18n.t('BankTransfer')}
              </Text>
            </View>
          </View>
          <View className={'withdraw-modal-button-wrapper'}>
            <Button
              onClick={onPressWithdrawMoney}
              className={'withdraw-modal-button'}
              style={{
                backgroundColor: AccountTheme.withdrawButton,
                color: AccountTheme.withdrawButtonText
              }}>
              {I18n.t('WithDrawMoney')}
            </Button>
          </View>
          {withdrawError != '' && (
            <Text
              className={'withdraw-modal-error-text'}
              style={{
                color: AccountTheme.error
              }}>
              {withdrawError}
            </Text>
          )}
        </View>
      </View>
    </BottomSheet>
  );
};
export default React.memo(WithdrawModal);
